// src/components/sections/TestimonialSection/TestimonialSection.tsx
import React from "react";
import styled from "styled-components";
import { useKeenSlider } from "keen-slider/react";
import { DefaultSection } from "./DefaultSection";
import { Typography } from "../../general/Typography";
import { Flex } from "../wrappers";

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  avatar?: string;
}

interface TestimonialSectionProps {
  testimonials: Testimonial[];
  title?: string;
  sectionId?: string;
  backgroundColor?: string;
  fullHeight?: boolean;
  snap?: boolean;
  animateOnScroll?: boolean;
}

const CarouselWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const KeenSlider = styled.div`
  display: flex;
  width: 100%;
`;

const Slide = styled.div`
  min-width: 60%;
  max-width: 90%;
  flex-shrink: 0;
  margin: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.elevations.md};
  gap: ${({ theme }) => theme.spacing.md};
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const TestimonialSection: React.FC<TestimonialSectionProps> = ({
  testimonials,
  title = "What people are saying",
  sectionId,
  backgroundColor,
  fullHeight = false,
  snap = false,
  animateOnScroll = true,
}) => {
  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: "snap",
    slides: {
      perView: 1,
    },
    breakpoints: {
      "(min-width: 640px)": {
        slides: { perView: 1.1 },
      },
      "(min-width: 768px)": {
        slides: { perView: 2 },
      },
      "(min-width: 1024px)": {
        slides: { perView: 3.5 },
      },
      "(min-width: 1280px)": {
        slides: { perView: 4 },
      },
    },
  });

  return (
    <DefaultSection
      sectionId={sectionId}
      title={title}
      fullHeight={fullHeight}
      backgroundColor={backgroundColor}
      snap={snap}
      animateOnScroll={animateOnScroll}
      content={
        <CarouselWrapper>
          <KeenSlider ref={sliderRef} className="keen-slider">
            {testimonials.map((t, i) => (
              <Slide key={i} className="keen-slider__slide">
                <Typography variant="body" align="center">“{t.quote}”</Typography>
                {t.avatar && <Avatar src={t.avatar} alt={t.name} />}
                <Typography variant="caption" align="center" style={{ fontWeight: 600 }}>{t.name}</Typography>
                <Typography variant="caption" align="center" style={{ color: "#888" }}>{t.role}</Typography>
              </Slide>
            ))}
          </KeenSlider>
        </CarouselWrapper>
      }
    />
  );
};